import { AuthStatusMessage } from '@Auth/components/AuthStatusMessage';
import { Button } from '@Pages/pageRuntime/components/ui/Button';
import { ErrorMessage } from '@Pages/pageRuntime/components/ui/ErrorMessage';
import { useTwoFactorSetup } from '@Pages/pageRuntime/hooks/useTwoFactorSetup';
import { cn } from '@Pages/pageRuntime/lib/utils';
import { apiErrorMessage } from '@Pages/pageRuntime/sdk/apiErrors';
import type { SdkTwoFactorSetupHandle, SdkTwoFactorSetupState } from '@Pages/pageRuntime/sdk/types';
import { type FormEventHandler, useState } from 'react';

import { lazyComponent } from './lazyComponent';
import {
  TwoFactorFactorForm,
  TwoFactorLinkButton,
  TwoFactorLoadError,
  TwoFactorLoading,
  TwoFactorRestart
} from './TwoFactorPanels';
import {
  TwoFactorBackupCodeList,
  TwoFactorSendCodeButton,
  TwoFactorSmsConsent
} from './TwoFactorSendControls';

const FormPhoneInput = lazyComponent(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/form/FormPhoneInput')).FormPhoneInput
}));

type TwoFactorSetupProps = {
  /**
   * The authenticatable resource. Defaults to the resource of the pending sign-in, then of
   * the signed-in member.
   */
  resourceId?: string;

  /** Called after a signed-in member's new MFA number is active. */
  onComplete?: () => void;

  /** Called after the visitor stopped the setup. */
  onCancel?: () => void;

  /** Additional classes for the container. */
  className?: string;

  /** Additional classes for the main buttons. */
  buttonClassName?: string;
};

type StepProps = {
  setup: SdkTwoFactorSetupHandle;
  state: SdkTwoFactorSetupState;
  resourceId?: string;
  errors: string[];
  buttonClassName?: string;
};

/**
 * MFA setup by SMS. After a sign-in whose account has no MFA number yet, it asks for the
 * number, SMS consent, and the code sent to it, then shows the first backup codes and
 * continues to the sign-in destination. For a signed-in member it changes the MFA number:
 * the member first proves the current number or a backup code, and the current number
 * stays active until the new one is verified. Place it on an account settings page for
 * the change; `Sdk.Components.LoginForm` already renders it after sign-in. It renders
 * nothing when no setup applies.
 *
 * @example
 * <Sdk.Components.TwoFactorSetup onComplete={() => Sdk.utils.notify.toast('MFA number changed', { variant: 'success' })} />
 */
export function TwoFactorSetup({
  resourceId,
  onComplete,
  onCancel,
  className,
  buttonClassName
}: TwoFactorSetupProps) {
  const setup = useTwoFactorSetup({ resourceId });

  if (!setup.isActive) {
    return null;
  }

  const cancel = () => {
    void setup.cancel().then(onCancel);
  };

  if (setup.restartRequired) {
    return (
      <TwoFactorRestart
        error={setup.actionError ?? setup.error}
        fallback="Your setup expired. Start again."
        label="Start again"
        onRestart={cancel}
        disabled={setup.isPending}
        className={className}
        buttonClassName={buttonClassName}
      />
    );
  }

  if (setup.error) {
    return (
      <TwoFactorLoadError
        error={setup.error}
        fallback="Two-factor setup did not load. Reload the page."
        className={className}
      />
    );
  }

  const state = setup.data;

  if (state === undefined) {
    return <TwoFactorLoading label="Loading two-factor setup" className={className} />;
  }

  const stepProps: StepProps = {
    setup,
    state,
    resourceId,
    errors: setup.actionError
      ? [apiErrorMessage(setup.actionError, 'That step did not complete.')]
      : [],
    buttonClassName
  };

  return (
    <div className={cn('grid gap-3', className)}>
      {state.step === 'complete' ? (
        <CompleteStep {...stepProps} onComplete={onComplete} />
      ) : (
        <>
          <OpenStep {...stepProps} />
          <div className="flex">
            <TwoFactorLinkButton onClick={cancel} disabled={setup.isPending}>
              {state.isReplacement ? 'Keep my current number' : 'Start over'}
            </TwoFactorLinkButton>
          </div>
        </>
      )}
    </div>
  );
}

function OpenStep(props: StepProps) {
  const { state, resourceId, buttonClassName } = props;

  return (
    <>
      <p className="text-sm text-muted-foreground">
        {state.isReplacement
          ? 'Your current number stays active until you verify the replacement.'
          : 'Verify your SMS number and save backup codes to protect your account.'}
      </p>
      {state.status ? (
        <AuthStatusMessage title="Verification">{state.status}</AuthStatusMessage>
      ) : null}
      {state.step === 'authorize' ? (
        <AuthorizeStep {...props} />
      ) : state.step === 'phone' ? (
        <PhoneStep {...props} />
      ) : state.step === 'consent' ? (
        <TwoFactorSmsConsent
          flow="setup"
          resourceId={resourceId}
          buttonClassName={buttonClassName}
        />
      ) : (
        <VerifyStep {...props} />
      )}
    </>
  );
}

function AuthorizeStep({ setup, state, resourceId, errors, buttonClassName }: StepProps) {
  const [usesBackupCode, setUsesBackupCode] = useState(false);
  const [value, setValue] = useState('');

  return (
    <>
      <p className="text-sm">
        Confirm your current MFA number ending {state.maskedPhone}, or use a backup code, before you
        change it.
      </p>
      {usesBackupCode ? null : (
        <TwoFactorSendCodeButton
          flow="setup"
          resourceId={resourceId}
          label="Text a code to my current number"
        />
      )}
      <TwoFactorFactorForm
        usesBackupCode={usesBackupCode}
        value={value}
        onChange={setValue}
        onSubmit={(factor) =>
          void setup
            .verifyExistingFactor(usesBackupCode ? { recoveryCode: factor } : { code: factor })
            .then(() => setValue(''))
        }
        submitLabel="Verify existing factor"
        errors={errors}
        disabled={setup.isPending}
        buttonClassName={buttonClassName}
      />
      <div className="flex">
        <TwoFactorLinkButton
          onClick={() => {
            setUsesBackupCode((current) => !current);
            setValue('');
          }}
        >
          {usesBackupCode ? 'Use my current phone instead' : 'Use a backup code instead'}
        </TwoFactorLinkButton>
      </div>
    </>
  );
}

function PhoneStep({ setup, errors, buttonClassName }: StepProps) {
  const [phone, setPhone] = useState('');

  const submit: FormEventHandler = (event) => {
    event.preventDefault();
    event.stopPropagation();

    void setup.submitPhone(phone);
  };

  return (
    <form onSubmit={submit} className="grid gap-3">
      <FormPhoneInput
        label="MFA phone number"
        defaultCountry="US"
        allowExtension={false}
        value={phone}
        onChange={setPhone}
        helpText="This is separate from your contact phone number."
        error={errors[0]}
      />
      <Button type="submit" className={buttonClassName} disabled={setup.isPending || phone === ''}>
        Continue
      </Button>
    </form>
  );
}

function VerifyStep({ setup, state, resourceId, errors, buttonClassName }: StepProps) {
  const [code, setCode] = useState('');

  return (
    <>
      <p className="text-sm">
        Enter the code sent to your new MFA number ending {state.maskedPhone}.
      </p>
      <TwoFactorFactorForm
        usesBackupCode={false}
        value={code}
        onChange={setCode}
        onSubmit={(value) => void setup.verifyNewNumber(value).then(() => setCode(''))}
        submitOnComplete
        submitLabel="Verify new number"
        errors={errors}
        disabled={setup.isPending}
        buttonClassName={buttonClassName}
      />
      <TwoFactorSendCodeButton
        flow="setup"
        resourceId={resourceId}
        label="Send a new code"
        variant="link"
      />
    </>
  );
}

function CompleteStep({
  setup,
  state,
  errors,
  buttonClassName,
  onComplete
}: StepProps & { onComplete?: () => void }) {
  const finish = () => {
    void setup.finish().then((finished) => {
      if (finished && state.isReplacement) {
        onComplete?.();
      }
    });
  };
  const finishControls = (
    <>
      <Button
        type="button"
        className={cn('w-full', buttonClassName)}
        disabled={setup.isPending}
        onClick={finish}
      >
        {setup.codes ? 'I saved my codes, continue' : 'Continue'}
      </Button>
      <ErrorMessage errors={errors} />
    </>
  );

  if (setup.codes) {
    return <TwoFactorBackupCodeList codes={setup.codes}>{finishControls}</TwoFactorBackupCodeList>;
  }

  return (
    <>
      <p className="text-sm">Your MFA phone number is verified.</p>
      {finishControls}
    </>
  );
}
